import { useState } from 'react';
import type { PdfAnnotation } from './PdfViewer';
import { formatDeadline } from './UrgencyBadge';
import UrgencyBadge from './UrgencyBadge';
import AiContextPanel from './AiContextPanel';

type Version = {
  id: number;
  version_number: number;
  filename: string;
  mime_type: string | null;
  size_bytes: number | null;
  file_key: string;
  uploaded_at: string;
  uploaded_by_name: string | null;
};

type Doc = {
  id: number;
  project_id: number;
  project_name: string;
  title: string;
  status: 'a_consulter' | 'a_traiter' | 'valide';
  urgency: 'normal' | 'urgent' | 'critique';
  deadline: string | null;
  notes: string | null;
};

const STATUS_LABEL: Record<string, string> = {
  a_consulter: 'À consulter',
  a_traiter: 'À traiter',
  valide: 'Validé',
};

type TabKey = 'infos' | 'comments' | 'versions' | 'assistant';

export default function DocSidePanelTabs({
  doc,
  versions,
  annotations,
  selectedVer,
  setSelectedVer,
  reviewNote,
  setReviewNote,
  onAddFreeComment,
  onDeleteAnnotation,
}: {
  doc: Doc;
  versions: Version[];
  annotations: PdfAnnotation[];
  selectedVer: Version | null;
  setSelectedVer: (v: Version) => void;
  reviewNote: string;
  setReviewNote: (s: string) => void;
  onAddFreeComment: (text: string) => void | Promise<void>;
  onDeleteAnnotation: (id: number) => void | Promise<void>;
}) {
  const [tab, setTab] = useState<TabKey>('infos');
  const [draft, setDraft] = useState('');

  return (
    <div className="doc-side-tabs">
      <div className="side-tabs">
        <button
          className={tab === 'infos' ? 'side-tab active' : 'side-tab'}
          onClick={() => setTab('infos')}
        >
          Infos
        </button>
        <button
          className={tab === 'comments' ? 'side-tab active' : 'side-tab'}
          onClick={() => setTab('comments')}
        >
          Comm.
        </button>
        <button
          className={tab === 'versions' ? 'side-tab active' : 'side-tab'}
          onClick={() => setTab('versions')}
        >
          Versions
        </button>
        <button
          className={tab === 'assistant' ? 'side-tab active' : 'side-tab'}
          onClick={() => setTab('assistant')}
        >
          Assistant
        </button>
      </div>

      {tab === 'infos' && (
        <div className="card">
          <h3 style={{ margin: '0 0 6px', fontSize: 16 }}>{doc.title}</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
            <UrgencyBadge level={doc.urgency} />
            <span className="card-meta">{STATUS_LABEL[doc.status]}</span>
          </div>

          <div className="card-meta">Projet</div>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>{doc.project_name}</div>

          <div className="card-meta">Échéance</div>
          <div style={{ marginBottom: 8 }}>{formatDeadline(doc.deadline)}</div>

          {selectedVer && (
            <>
              <div className="card-meta">Version affichée</div>
              <div style={{ marginBottom: 8 }}>
                v{selectedVer.version_number} — {selectedVer.filename}
                <div className="card-meta" style={{ marginTop: 2 }}>
                  {selectedVer.uploaded_by_name ?? '—'} ·{' '}
                  {new Date(selectedVer.uploaded_at).toLocaleString('fr-FR')}
                </div>
              </div>
            </>
          )}

          {doc.notes && (
            <>
              <div className="card-meta">Notes</div>
              <div style={{ marginBottom: 10 }}>{doc.notes}</div>
            </>
          )}

          <div className="card-meta" style={{ marginTop: 10 }}>
            Note attachée au retour « À revoir »
          </div>
          <textarea
            rows={3}
            placeholder="Facultatif — sera ajoutée comme commentaire quand tu cliques À revoir"
            value={reviewNote}
            onChange={(e) => setReviewNote(e.target.value)}
            style={{
              width: '100%',
              padding: 8,
              marginTop: 6,
              border: '1px solid var(--c-border)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--c-bg)',
              resize: 'vertical',
            }}
          />
        </div>
      )}

      {tab === 'comments' && (
        <div className="card">
          <div className="card-meta" style={{ marginBottom: 6 }}>
            Les commentaires ancrés au PDF apparaissent aussi sur les pages.
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              placeholder="Nouveau commentaire"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              style={{
                flex: 1,
                padding: 8,
                border: '1px solid var(--c-border)',
                borderRadius: 'var(--radius-sm)',
              }}
            />
            <button
              className="btn btn-primary"
              onClick={async () => {
                await onAddFreeComment(draft);
                setDraft('');
              }}
            >
              Ajouter
            </button>
          </div>
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {annotations.length === 0 && <div className="card-meta">Aucun commentaire.</div>}
            {annotations.map((a) => (
              <div key={a.id} style={{ borderTop: '1px solid var(--c-border)', paddingTop: 8 }}>
                <div
                  className="card-meta"
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <span>
                    {a.user_name} · {new Date(a.created_at).toLocaleString('fr-FR')}
                    {a.page ? ` · page ${a.page}` : ''}
                  </span>
                  <button
                    onClick={() => onDeleteAnnotation(a.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--c-muted)',
                      cursor: 'pointer',
                      fontSize: 12,
                    }}
                    title="Supprimer"
                  >
                    ×
                  </button>
                </div>
                {a.type === 'vocal' && a.voice_key ? (
                  <audio
                    controls
                    src={`/api/v1/files?key=${encodeURIComponent(a.voice_key)}`}
                    style={{ width: '100%', marginTop: 4 }}
                  />
                ) : a.type === 'surlignage' ? (
                  <div style={{ fontStyle: 'italic' }}>
                    ▭ Surligné{a.content ? ` — « ${a.content} »` : ''}
                  </div>
                ) : (
                  <div>{a.content}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'versions' && (
        <div className="card">
          {versions.length === 0 && <div className="card-meta">Aucune version.</div>}
          {versions.map((v) => (
            <div
              key={v.id}
              onClick={() => setSelectedVer(v)}
              style={{
                padding: 10,
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                background: selectedVer?.id === v.id ? 'var(--c-nav)' : 'transparent',
                borderTop: '1px solid var(--c-border)',
              }}
            >
              <div style={{ fontWeight: 600 }}>
                v{v.version_number} — {v.filename}
              </div>
              <div className="card-meta">
                {v.uploaded_by_name ?? '—'} · {new Date(v.uploaded_at).toLocaleString('fr-FR')}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'assistant' && (
        <AiContextPanel
          contextLabel={`Document « ${doc.title} »${
            selectedVer ? ` (v${selectedVer.version_number})` : ''
          }`}
          contextKind="document"
          projectId={doc.project_id}
          documentId={doc.id}
        />
      )}
    </div>
  );
}
